import React from 'react';
import {
  SpellCheck,
  LayoutDashboard,
  Library,
  Focus,
  UsersRound,
  BookUp,
  type LucideIcon,
} from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { AlsoIncluded } from './AlsoIncluded';
import suggestionImg from '../../assets/landing/suggestion.svg';
import planningImg from '../../assets/landing/planning.svg';
import shareImg from '../../assets/landing/share.png';
import exportImg from '../../assets/landing/export.png';

const LANDING_FEATURE_SCREENSHOTS = '/screenshots/landing_features';

interface Feature {
  titleKey: string;
  altKey: string;
  renderBody: (t: (k: string) => string) => React.ReactNode;
  screenshotFilename: string;
  screenshotOverride?: string;
  Icon: LucideIcon;
}

interface FeaturesProps {
  logoUrl: string;
}

export const Features: React.FC<FeaturesProps> = () => {
  const { t } = useLocale();

  const hl = (text: string) => (
    <strong style={{ color: 'var(--scribane-green)', fontWeight: 700 }}>{text}</strong>
  );

  const features: Feature[] = [
    {
      titleKey: 'landing_feature_write_title',
      altKey: 'landing_feature_write_alt',
      renderBody: (t) => <>
        {t('landing_feature_write_before_hl')}
        {hl(t('landing_feature_write_hl_1'))}
        {t('landing_feature_write_mid')}
        {hl(t('landing_feature_write_hl_2'))}
        {t('landing_feature_write_after_hl')}
      </>,
      screenshotFilename: 'Scribane-add_note.webp',
      Icon: Focus,
    },
    {
      titleKey: 'landing_feature_revision_title',
      altKey: 'landing_feature_revision_alt',
      renderBody: (t) => <>
        {t('landing_feature_revision_description_lead')}{' '}
        {t('landing_feature_revision_tail_prefix')}{hl(t('landing_feature_revision_hl_1'))}
      </>,
      screenshotFilename: 'Scribane-suggestion_es.webp',
      screenshotOverride: suggestionImg,
      Icon: SpellCheck,
    },
    {
      titleKey: 'landing_feature_structure_title',
      altKey: 'landing_feature_structure_alt',
      renderBody: (t) => <>
        {hl(t('landing_feature_structure_hl_1'))}
        {t('landing_feature_structure_mid_1')}
        {hl(t('landing_feature_structure_hl_2'))}
        {t('landing_feature_structure_rest')}
      </>,
      screenshotFilename: 'Scribane-planning_es.webp',
      screenshotOverride: planningImg,
      Icon: LayoutDashboard,
    },
    {
      titleKey: 'landing_feature_materials_title',
      altKey: 'landing_feature_materials_alt',
      renderBody: (t) => <>
        {t('landing_feature_materials_before_hl')}
        {hl(t('landing_feature_materials_hl_1'))}
        {t('landing_feature_materials_mid')}
        {hl(t('landing_feature_materials_hl_2'))}
      </>,
      screenshotFilename: 'Scribane-resources_es.webp',
      Icon: Library,
    },
    {
      titleKey: 'landing_feature_collaboration_title',
      altKey: 'landing_feature_collaboration_alt',
      renderBody: (t) => <>
        {t('landing_feature_collaboration_description_lead')}{' '}
        {hl(t('landing_feature_collaboration_hl_1'))}
        {t('landing_feature_collaboration_mid_1')}
        {hl(t('landing_feature_collaboration_hl_2'))}
        {t('landing_feature_collaboration_after_hl')}
      </>,
      screenshotFilename: 'Scribane-comment_es.webp',
      screenshotOverride: shareImg,
      Icon: UsersRound,
    },
    {
      titleKey: 'landing_feature_workflow_title',
      altKey: 'landing_feature_workflow_alt',
      renderBody: (t) => <>
        {t('landing_feature_workflow_before_hl')}
        {hl(t('landing_feature_workflow_hl_1'))}
        {t('landing_feature_workflow_after_hl')}
      </>,
      screenshotFilename: 'Scribane-timeline_es.webp',
      screenshotOverride: exportImg,
      Icon: BookUp,
    },
  ];

  return (
    <section id="features" className="scroll-mt-24 bg-white" style={{ padding: '110px 0 120px' }}>
      <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>

        <div style={{ textAlign: 'center', maxWidth: '760px', margin: '0 auto 64px' }}>
          <p
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: '16px',
              lineHeight: '20px',
              fontWeight: 500,
              letterSpacing: '3.2px',
              textTransform: 'uppercase',
              color: 'var(--scribane-green)',
              margin: '0 0 22px',
            }}
          >
            {t('landing_features_aux')}
          </p>
          <h2
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontWeight: 400,
              fontSize: 'clamp(34px, 5vw, 56px)',
              lineHeight: 1.06,
              letterSpacing: '-0.02em',
              margin: 0,
              color: 'rgb(39,39,41)',
            }}
          >
            <strong style={{ fontWeight: 800 }}>{t('landing_features_title_bold')}</strong>
            {' '}{t('landing_features_title_rest')}
          </h2>
          <p
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: '18px',
              lineHeight: '24px',
              fontWeight: 400,
              letterSpacing: '0px',
              color: 'rgba(39,39,41,0.7)',
              margin: '22px auto 0',
              maxWidth: '620px',
            }}
          >
            {t('landing_features_subtitle')}
          </p>
        </div>

        <style>{`
          .feature-card {
            transition: transform 220ms cubic-bezier(0.22,1,0.36,1),
                        box-shadow 220ms cubic-bezier(0.22,1,0.36,1),
                        border-color 220ms;
          }
          .feature-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 32px rgba(39,39,41,0.10), 0 2px 6px rgba(39,39,41,0.05);
            border-color: rgba(39,39,41,0.18);
          }
        `}</style>
        <div className="grid grid-cols-1 gap-7 max-w-[520px] mx-auto min-[800px]:grid-cols-2 min-[800px]:max-w-none min-[800px]:mx-0 min-[1000px]:grid-cols-3">
          {features.map((feature, index) => {
            const src = feature.screenshotOverride ?? `${LANDING_FEATURE_SCREENSHOTS}/${feature.screenshotFilename}`;
            const { Icon } = feature;
            return (
              <article
                key={index}
                className="feature-card"
                style={{
                  border: '1px solid #e7e7e8',
                  borderRadius: '20px',
                  paddingTop: '36px',
                  paddingLeft: '34px',
                  paddingRight: '34px',
                  paddingBottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  background: '#fff',
                  minHeight: '540px',
                }}
              >
                <div
                  style={{
                    width: '54px',
                    height: '54px',
                    borderRadius: '14px',
                    background: '#effaf2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--scribane-green)',
                    marginBottom: '34px',
                    flexShrink: 0,
                  }}
                >
                  <Icon size={24} strokeWidth={2} />
                </div>

                <h3
                  style={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: '21px',
                    fontWeight: 700,
                    letterSpacing: '0.01em',
                    textTransform: 'uppercase',
                    margin: '0 0 18px',
                    color: 'rgb(39,39,41)',
                    lineHeight: 1.2,
                  }}
                >
                  {t(feature.titleKey)}
                </h3>

                <p
                  style={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: '14px',
                    lineHeight: '26px',
                    fontWeight: 400,
                    letterSpacing: '0px',
                    color: '#686869',
                    margin: '0 0 26px',
                  }}
                >
                  {feature.renderBody(t)}
                </p>

                <div style={{ marginTop: 'auto' }}>
                  <div
                    style={{
                      aspectRatio: '1.4',
                      border: '1px solid #e7e7e8',
                      borderBottom: 'none',
                      borderRadius: '12px 12px 0 0',
                      overflow: 'hidden',
                      background: '#fcfcfc',
                      boxShadow: '0 -1px 0 rgba(24,24,27,.02), 0 24px 50px -20px rgba(24,24,27,.28), 0 8px 20px -12px rgba(24,24,27,.16)',
                    }}
                  >
                    <img
                      src={src}
                      alt={t(feature.altKey)}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                    />
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <AlsoIncluded />
      </div>
    </section>
  );
};
